import {
  Badge,
  Button,
  Checkbox,
  Icon,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa6';
import { Link as RouterLink } from 'react-router-dom';
import { formatDocument } from 'shared/helpers/document';
import { onlyNumbers } from 'shared/helpers/string';
import type {
  Cashback,
  CashbackSortField,
  CashbackSortOrder,
} from 'services/interfaces/cashback';
import { formatCurrencyBrl, formatDateShort } from '../helpers/formatCashback';

interface CashbackTableProps {
  rows: Cashback[];
  sortBy: CashbackSortField;
  sortOrder: CashbackSortOrder;
  onSort: (field: CashbackSortField) => void;
  onOpenPayment: (row: Cashback) => void;
  actionLoadingId: number | null;
  listLoading?: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: () => void;
  canUpdate?: boolean;
}

function SortIcon({
  field,
  sortBy,
  sortOrder,
}: {
  field: CashbackSortField;
  sortBy: CashbackSortField;
  sortOrder: CashbackSortOrder;
}) {
  if (sortBy !== field) {
    return <Icon as={FaSort} opacity={0.35} boxSize={3} />;
  }
  return sortOrder === 'asc' ? (
    <Icon as={FaSortUp} boxSize={3} />
  ) : (
    <Icon as={FaSortDown} boxSize={3} />
  );
}

const CashbackTable: React.FC<CashbackTableProps> = ({
  rows,
  sortBy,
  sortOrder,
  onSort,
  onOpenPayment,
  actionLoadingId,
  listLoading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  canUpdate = false,
}) => {
  const allSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = rows.some((r) => selectedIds.has(r.id));

  return (
    <TableContainer overflowX="auto">
      <Table
        variant="simple"
        size="sm"
        sx={{
          'thead th, tbody td': { textAlign: 'left' },
        }}
      >
        <Thead>
          <Tr>
            <Th w="40px" px={2}>
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={someSelected && !allSelected}
                onChange={onToggleSelectAll}
                colorScheme="yooga"
                size="sm"
              />
            </Th>
            <Th textAlign="left">IDI</Th>
            <Th
              textAlign="left"
              cursor="pointer"
              userSelect="none"
              onClick={() => onSort('requested_at')}
            >
              Data solicitação{' '}
              <SortIcon
                field="requested_at"
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </Th>
            <Th textAlign="left">Empresa</Th>
            <Th textAlign="left">Documento</Th>
            <Th textAlign="left">Chave Pix</Th>
            <Th
              textAlign="left"
              cursor="pointer"
              userSelect="none"
              onClick={() => onSort('cashback_amount')}
            >
              Valor{' '}
              <SortIcon
                field="cashback_amount"
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </Th>
            <Th
              textAlign="left"
              cursor="pointer"
              userSelect="none"
              onClick={() => onSort('paid')}
            >
              Status{' '}
              <SortIcon field="paid" sortBy={sortBy} sortOrder={sortOrder} />
            </Th>
            <Th textAlign="left">Data pagamento</Th>
            {canUpdate && <Th textAlign="left">Ações</Th>}
          </Tr>
        </Thead>
        <Tbody opacity={listLoading ? 0.45 : 1}>
          {rows.map((row) => {
            const customerDoc = onlyNumbers(row.document);
            return (
              <Tr key={row.id}>
                <Td px={2}>
                  <Checkbox
                    isChecked={selectedIds.has(row.id)}
                    onChange={() => onToggleSelect(row.id)}
                    colorScheme="yooga"
                    size="sm"
                  />
                </Td>
                <Td textAlign="left">{row.idi}</Td>
                <Td textAlign="left">{formatDateShort(row.requested_at)}</Td>
                <Td
                  maxW="200px"
                  isTruncated
                  title={row.company_name}
                  textAlign="left"
                >
                  {customerDoc ? (
                    <Link
                      as={RouterLink}
                      to={`/customer/${customerDoc}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="yooga.500"
                      fontWeight="medium"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {row.company_name}
                    </Link>
                  ) : (
                    row.company_name
                  )}
                </Td>
                <Td whiteSpace="nowrap" textAlign="left">
                  {formatDocument(row.document)}
                </Td>
                <Td
                  maxW="180px"
                  isTruncated
                  title={row.pix_key}
                  textAlign="left"
                >
                  {row.pix_key}
                </Td>
                <Td textAlign="left">
                  {formatCurrencyBrl(row.cashback_amount)}
                </Td>
                <Td textAlign="left">
                  <Badge
                    colorScheme={row.paid ? 'green' : 'yellow'}
                    variant="subtle"
                  >
                    {row.paid ? 'Pago' : 'Pendente'}
                  </Badge>
                </Td>
                <Td textAlign="left">{formatDateShort(row.payment_date)}</Td>
                {canUpdate && (
                  <Td textAlign="left">
                    <Button
                      size="xs"
                      colorScheme="yooga"
                      variant={row.paid ? 'outline' : 'solid'}
                      isLoading={actionLoadingId === row.id}
                      isDisabled={
                        actionLoadingId !== null && actionLoadingId !== row.id
                      }
                      onClick={() => onOpenPayment(row)}
                    >
                      {row.paid ? 'Editar pagamento' : 'Marcar como pago'}
                    </Button>
                  </Td>
                )}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default CashbackTable;
